import {
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type RowData,
  TableController,
  type SortingState,
  getSortedRowModel,
} from "@tanstack/lit-table";
import { html, unsafeCSS, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import EkoElement from "@/utilities/element";
import "./table-light-dom.css"; // Light DOM styles
import style from "./table-shadow-dom.css?inline"; // Shadow DOM styles

import "@/components/checkbox";
import "@/components/icon";

@customElement("eko-table")
class Table extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @state()
  private _rowSelection: Record<string, boolean> = {};

  @state()
  private _sorting: SortingState = [];

  private tableController = new TableController(this);

  @property({ reflect: true }) class: string = "";
  @property({ reflect: false }) data: RowData[] = [];
  @property({ reflect: false }) defaultSortColumn: string = "";
  @property({ reflect: false }) defaultSortDirection: "asc" | "desc" = "desc";
  // By default tables are scrollable on the x-axis but will clip content on the y-axis,
  // the fixed prop allows us to disable that which is required for table cells that contain dropdowns.
  @property({ reflect: true, type: Boolean }) fixed: boolean = false;
  @property({ reflect: false }) schema: ColumnDef<unknown, any>[] = [];
  @property({ reflect: true, type: Boolean }) selectable: boolean = false;
  @property({ reflect: true }) variant: "default" | "borderless" = "default";

  private setDefaultSorting() {
    if (this.defaultSortColumn) {
      this._sorting = [{ desc: this.defaultSortDirection === "desc", id: this.defaultSortColumn }];
    }
  }

  firstUpdated() {
    this.setDefaultSorting();
  }

  public getSelectedColumnValues(columnKey: string) {
    return this.table
      .getSelectedRowModel()
      .rows.map((row) => row.getValue(columnKey) || (row.original as any)[columnKey]);
  }

  public getSelectedRowsData() {
    return this.table.getSelectedRowModel().rows.map((row) => row.original) || [];
  }

  render() {
    if (this.data.length && this.schema.length) {
      return this.renderDataTable();
    }
    return html`<div class="eko-table__wrapper">
      <slot></slot>
    </div>`;
  }

  renderDataTable() {
    return html`<div class="eko-table__wrapper">
      <table>
        <thead>
          ${repeat(
            this.table.getHeaderGroups(),
            (headerGroup) => headerGroup.id,
            (headerGroup) => html`
              <tr>
                ${headerGroup.headers.map(
                  (header) => html`
                    <th
                      colspan="${header.colSpan}"
                      style=${`width: ${header.column.getSize()}px; ${Object.entries(
                        (header.column.columnDef?.meta as any)?.style || {}
                      )
                        .map(
                          ([key, value]) =>
                            `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value}`
                        )
                        .join(";")}`}>
                      ${header.isPlaceholder
                        ? null
                        : html`<div
                            title=${header.column.getCanSort()
                              ? header.column.getNextSortingOrder() === "asc"
                                ? "Sort ascending"
                                : header.column.getNextSortingOrder() === "desc"
                                  ? "Sort descending"
                                  : "Clear sort"
                              : undefined}
                            @click="${header.column.getToggleSortingHandler()}"
                            class=${header.column.getCanSort()
                              ? "eko-table__header-sort"
                              : "eko-table__header-no-sort"}>
                            ${flexRender(header.column.columnDef.header, header.getContext())}
                            ${(header.column.getIsSorted() as string)
                              ? html`<eko-icon
                                  name="arrow-up"
                                  aria-sort=${header.column.getIsSorted() as string}
                                  class="eko-table__header-sort-icon"
                                  size="sm"></eko-icon>`
                              : null}
                          </div>`}
                    </th>
                  `
                )}
              </tr>
            `
          )}
        </thead>
        <tbody>
          ${this.table.getRowModel().rows.map(
            (row) =>
              html`<tr>
                ${row.getVisibleCells().map(
                  (cell) =>
                    html`<td
                      style=${`width: ${cell.column.getSize()}px; ${Object.entries(
                        (cell.column.columnDef?.meta as any)?.style || {}
                      )
                        .map(
                          ([key, value]) =>
                            `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value}`
                        )
                        .join(";")}`}>
                      ${flexRender(cell.column.columnDef.cell, { ...cell.getContext(), html })}
                    </td>`
                )}
              </tr>`
          )}
        </tbody>
      </table>
    </div>`;
  }

  updated(changedProperties: PropertyValues) {
    if (
      changedProperties.has("defaultSortColumn") ||
      changedProperties.has("defaultSortDirection")
    ) {
      this.setDefaultSorting();
    }
  }

  get table() {
    return this.tableController.table({
      columns: this.tableColumnsSchema,
      data: this.data,
      enableRowSelection: true,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onRowSelectionChange: (updaterOrValue) => {
        if (typeof updaterOrValue === "function") {
          this._rowSelection = updaterOrValue(this._rowSelection);
        } else {
          this._rowSelection = updaterOrValue;
        }

        this.dispatchEvent(
          new CustomEvent("selected", {
            detail: this.getSelectedRowsData(),
          })
        );
      },
      onSortingChange: (updaterOrValue) => {
        if (typeof updaterOrValue === "function") {
          this._sorting = updaterOrValue(this._sorting);
        } else {
          this._sorting = updaterOrValue;
        }
      },
      state: {
        rowSelection: this._rowSelection,
        sorting: this._sorting,
      },
    });
  }

  get tableColumnsSchema() {
    if (this.selectable) {
      return [
        {
          cell: ({ row }: { row: any }) => {
            const input = document.createElement("eko-checkbox");
            input.addEventListener("change", row.getToggleSelectedHandler());
            input.checked = row.getIsSelected();
            input.disabled = !row.getCanSelect();
            return input;
          },
          header: ({ table }: { table: any }) => {
            const input = document.createElement("eko-checkbox");
            input.addEventListener("change", table.getToggleAllRowsSelectedHandler());
            input.checked = table.getIsAllRowsSelected();
            return input;
          },
          id: "select",
          size: 30,
        },
        ...this.schema,
      ];
    }
    return this.schema;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-table": Table;
  }
}

export default Table;
